import React from 'react';
import { Ruler, Minus, Plus, TreePalm as PalmTree, Home } from 'lucide-react';
import { expositionOptions } from '../../lib/constants';
import type { StepProps } from './types';

export default function PropertyCharacteristicsStep({ 
  formData, 
  handleInputChange, 
  handleNumberChange,
  errors 
}: StepProps) {
  return (
    <div className="space-y-8">
      <h3 className="text-lg font-medium text-gray-900">
        Caractéristiques de votre bien
      </h3>
      
      <div className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Surface (m²)*
          </label>
          <div className="relative">
            <Ruler className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
            <input
              type="number"
              name="surface"
              value={formData.surface || ''}
              onChange={handleInputChange}
              className={`pl-10 w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 ${
                errors.surface ? 'border-red-500' : ''
              }`}
              required
              min="1"
            />
          </div>
          {errors.surface && (
            <p className="mt-1 text-sm text-red-600">{errors.surface}</p>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nombre de pièces*
            </label>
            <div className="flex items-center">
              <button
                type="button"
                onClick={() => handleNumberChange('nb_pieces', false)}
                className="p-2 border border-gray-300 rounded-l-lg hover:bg-gray-50"
              >
                <Minus className="h-4 w-4" />
              </button>
              <input
                type="number"
                name="nb_pieces"
                value={formData.nb_pieces || ''}
                onChange={handleInputChange}
                className={`w-16 text-center border-y border-gray-300 ${
                  errors.nb_pieces ? 'border-red-500' : ''
                }`}
                min="1"
              />
              <button
                type="button"
                onClick={() => handleNumberChange('nb_pieces', true)}
                className="p-2 border border-gray-300 rounded-r-lg hover:bg-gray-50"
              >
                <Plus className="h-4 w-4" />
              </button>
            </div>
            {errors.nb_pieces && (
              <p className="mt-1 text-sm text-red-600">{errors.nb_pieces}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nombre de chambres*
            </label>
            <div className="flex items-center">
              <button
                type="button"
                onClick={() => handleNumberChange('nb_chambres', false)}
                className="p-2 border border-gray-300 rounded-l-lg hover:bg-gray-50"
              >
                <Minus className="h-4 w-4" />
              </button>
              <input
                type="number"
                name="nb_chambres"
                value={formData.nb_chambres || ''}
                onChange={handleInputChange}
                className={`w-16 text-center border-y border-gray-300 ${
                  errors.nb_chambres ? 'border-red-500' : ''
                }`}
                min="1"
              />
              <button
                type="button"
                onClick={() => handleNumberChange('nb_chambres', true)}
                className="p-2 border border-gray-300 rounded-r-lg hover:bg-gray-50"
              >
                <Plus className="h-4 w-4" />
              </button>
            </div>
            {errors.nb_chambres && (
              <p className="mt-1 text-sm text-red-600">{errors.nb_chambres}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nombre de salles de bain*
            </label>
            <div className="flex items-center">
              <button
                type="button"
                onClick={() => handleNumberChange('nbr_salle_de_bain', false)}
                className="p-2 border border-gray-300 rounded-l-lg hover:bg-gray-50"
              >
                <Minus className="h-4 w-4" />
              </button>
              <input
                type="number"
                name="nbr_salle_de_bain"
                value={formData.nbr_salle_de_bain || ''}
                onChange={handleInputChange}
                className={`w-16 text-center border-y border-gray-300 ${
                  errors.nbr_salle_de_bain ? 'border-red-500' : ''
                }`}
                min="1"
              />
              <button
                type="button"
                onClick={() => handleNumberChange('nbr_salle_de_bain', true)}
                className="p-2 border border-gray-300 rounded-r-lg hover:bg-gray-50"
              >
                <Plus className="h-4 w-4" />
              </button>
            </div>
            {errors.nbr_salle_de_bain && (
              <p className="mt-1 text-sm text-red-600">{errors.nbr_salle_de_bain}</p>
            )}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Exposition*
          </label>
          <select
            name="exposition"
            value={formData.exposition}
            onChange={handleInputChange}
            className={`w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 ${
              errors.exposition ? 'border-red-500' : ''
            }`}
            required
          >
            {expositionOptions.map(({ value, label }) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
          {errors.exposition && (
            <p className="mt-1 text-sm text-red-600">{errors.exposition}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Jardin (m²)
          </label>
          <div className="relative">
            <PalmTree className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
            <input
              type="number"
              name="jardin"
              value={formData.jardin || ''}
              onChange={handleInputChange}
              className="pl-10 w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              placeholder="Surface en m²"
              min="0"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Balcon/Terrasse (m²)
          </label>
          <div className="relative">
            <Home className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
            <input
              type="number"
              name="balcon_terasse"
              value={formData.balcon_terasse || ''}
              onChange={handleInputChange}
              className="pl-10 w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              placeholder="Surface en m²"
              min="0"
            />
          </div>
        </div>

        <div className="space-y-4">
          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              name="cave"
              checked={formData.cave}
              onChange={handleInputChange}
              className="w-5 h-5 rounded border-gray-300 
                checked:bg-gradient-to-r checked:from-[#2A4365] checked:via-[#4A5568] checked:to-[#C4A052]
                focus:ring-[#2A4365] focus:ring-offset-2 transition-all duration-300"
            />
            <span className="text-sm text-gray-700">Cave</span>
          </label>

          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              name="garage"
              checked={formData.garage}
              onChange={handleInputChange}
              className="w-5 h-5 rounded border-gray-300 
                checked:bg-gradient-to-r checked:from-[#2A4365] checked:via-[#4A5568] checked:to-[#C4A052]
                focus:ring-[#2A4365] focus:ring-offset-2 transition-all duration-300"
            />
            <span className="text-sm text-gray-700">Garage</span>
          </label>
        </div>
      </div>
    </div>
  );
}